import React, { useRef } from 'react';
import { FieldArray, FieldArrayRenderProps, Formik, FormikProps } from 'formik';
import { Modal, Row, Col, Button, Divider } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { Form } from 'formik-antd';
import * as Yup from 'yup';
import cx from 'classnames';

import ContactsSection, {
  contactsValidationSchema,
} from 'components/modals/sections/ContactsSection';
import SubmitButton from 'components/formik/SubmitButton';
import BaseAlert from 'components/wrappers/BaseAlert';
import { AdditionalInfoSectionEnum } from 'features/account/enums';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { updatePrimaryContactInfo } from 'redux/reducers/accountSlice';
import { updateCorporateTAPrimaryContactInfo } from 'redux/reducers/recruiterSlice';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';
import { BaseModal, BaseModalFooter } from 'components/wrappers/BaseModal';
import {
  PreferredContactMethodEnum,
  PrimaryContactForEnum,
  ProfileCorporatePrimaryContactInfoModel,
} from 'api/backofficeApiClient';
import useCanRemoveContact from 'features/account/AdditionalInfo/hooks/useCanRemoveContact';
import {
  isAddButtonVisible,
  isRemoveButtonVisible,
} from 'utils/fieldArrayUtils';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';

import styles from './EditPrimaryContactInfoModal.module.less';

type Props = {
  onClose: () => void;
  onEdit: () => Promise<void>;
  companyId?: string;
  primaryContactInfo?: ProfileCorporatePrimaryContactInfoModel[];
};

interface PrimaryContactFormValues {
  id?: string;
  firstName: string;
  lastName: string;
  title: string;
  email: string;
  phone: string;
  preferredContactMethod?: PreferredContactMethodEnum[];
  contactFor?: PrimaryContactForEnum[];
}

interface PrimaryContactsInfoFormValues {
  primaryContacts: Array<PrimaryContactFormValues>;
}

const validationSchema = Yup.object({
  primaryContacts: Yup.array().of(
    Yup.object({
      ...contactsValidationSchema,
      firstName:
        contactsValidationSchema.firstName.required('Field is required'),
      lastName: contactsValidationSchema.lastName.required('Field is required'),
      title: contactsValidationSchema.title.required('Field is required'),
      email: contactsValidationSchema.email.required('Field is required'),
      phone: contactsValidationSchema.phone.required('Field is required'),
    }),
  ),
});

const PrimaryContactsFieldArray = React.memo(
  (props: FieldArrayRenderProps) => {
    const { primaryContacts } = (
      props.form as FormikProps<PrimaryContactsInfoFormValues>
    ).values;
    const [canRemoveOptions, checkCanRemoveContact] = useCanRemoveContact(
      AdditionalInfoSectionEnum.PrimaryContact,
    );

    const handleRemoveContact = async (index: number) => {
      const { id } = primaryContacts[index];
      const canContactBeRemoved = await checkCanRemoveContact(id);

      canContactBeRemoved && removeContactFromArray(index);
    };

    const removeContactFromArray = (index: number) => {
      props.remove(index);
    };

    const isContactChecking = (primaryContactId?: string) => {
      const { isLoading, contactId } = canRemoveOptions;

      return isLoading && contactId === primaryContactId;
    };

    return (
      <>
        {primaryContacts.length > 0 &&
          primaryContacts.map(
            (primaryContact: PrimaryContactFormValues, index: number) => (
              <div key={index} className={styles.primaryContactSection}>
                <ContactsSection
                  index={index}
                  formNamePrefix={props.name}
                  showPreferredContactMethod
                  showContactFor
                  preferredContactMethodRequired={false}
                  contactForRequired={false}
                  actionButtons={
                    <>
                      {isAddButtonVisible<PrimaryContactFormValues>(
                        primaryContacts,
                        index,
                      ) && (
                        <Button
                          className={cx('borderless-btn', styles.btn)}
                          onClick={() =>
                            props.push({
                              firstName: '',
                              lastName: '',
                              title: '',
                              email: '',
                              phone: '',
                              preferredContactMethod: [],
                              contactFor: [],
                            })
                          }
                          icon={<span className="icon icon-plus-filled"></span>}
                        />
                      )}

                      {isRemoveButtonVisible<PrimaryContactFormValues>(
                        primaryContacts,
                      ) && (
                        <Button
                          disabled={canRemoveOptions.isLoading}
                          className={cx('borderless-btn', styles.btn)}
                          onClick={() => handleRemoveContact(index)}
                          icon={
                            isContactChecking(primaryContact.id) ? (
                              <LoadingOutlined />
                            ) : (
                              <span className="icon icon-closed-filled" />
                            )
                          }
                        />
                      )}
                    </>
                  }
                />

                <Divider className="modal-divider after-form" />
              </div>
            ),
          )}
      </>
    );
  },
  (prevProps: FieldArrayRenderProps, nextProps: FieldArrayRenderProps) => {
    return (
      (prevProps.form.values as PrimaryContactsInfoFormValues).primaryContacts
        .length ===
      (nextProps.form.values as PrimaryContactsInfoFormValues).primaryContacts
        .length
    );
  },
);

export default function EditPrimaryContactInfoModal(props: Props) {
  const { companyId, primaryContactInfo, onClose, onEdit } = props;
  const dispatch = useAppDispatch();
  const isAdmin = useAppSelector(isAdminSelector);
  const formRef: any = useRef();
  const initialValues: PrimaryContactsInfoFormValues = {
    primaryContacts: !!primaryContactInfo?.length
      ? primaryContactInfo.map(
          (primaryContact): PrimaryContactFormValues => ({
            id: primaryContact.contactId,
            firstName: primaryContact.firstName || '',
            lastName: primaryContact.lastName || '',
            email: primaryContact.email || '',
            title: primaryContact.title || '',
            phone: primaryContact.phoneNumber || '',
            preferredContactMethod: primaryContact.preferredContactMethod ?? [],
            contactFor: primaryContact.contactFor ?? [],
          }),
        )
      : [
          {
            firstName: '',
            lastName: '',
            title: '',
            email: '',
            phone: '',
            preferredContactMethod: [],
            contactFor: [],
          },
        ],
  };

  const handleSubmit = async (values: PrimaryContactsInfoFormValues) => {
    const { primaryContacts } = values;

    const primaryContactsInfo = primaryContacts.map(
      (primaryContact): ProfileCorporatePrimaryContactInfoModel => ({
        contactId: primaryContact.id,
        firstName: primaryContact.firstName || undefined,
        lastName: primaryContact.lastName || undefined,
        email: primaryContact.email || undefined,
        title: primaryContact.title || undefined,
        phoneNumber: primaryContact.phone || undefined,
        preferredContactMethod: primaryContact.preferredContactMethod?.length
          ? primaryContact.preferredContactMethod
          : undefined,
        contactFor: primaryContact.contactFor?.length
          ? primaryContact.contactFor
          : undefined,
      }),
    );

    const correctAction = isAdmin
      ? updateCorporateTAPrimaryContactInfo
      : updatePrimaryContactInfo;
    const requestData = {
      companyId: isAdmin ? companyId : undefined,
      primaryContactsData: { primarycontacts: primaryContactsInfo },
    };
    const result = await dispatch(correctAction(requestData));

    if (correctAction.fulfilled.match(result)) {
      await onEdit();

      BaseAlert.success('Primary contact information saved.');
      onClose();
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const handleModalCancel = () => {
    if (formRef.current?.isSubmitting) return;

    if (formRef.current?.dirty) {
      return Modal.confirm({
        title: 'Are you sure? The data will not be saved.',
        icon: <span className="icon icon-attention-filled" />,
        onOk() {
          onClose();
        },
        onCancel() {},
      });
    }
    onClose();
  };

  return (
    <BaseModal
      title="Edit primary contact information"
      maxWidth={888}
      open={true}
      onCancel={handleModalCancel}
    >
      <Formik
        enableReinitialize
        innerRef={formRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        <Form layout="vertical">
          <div className="modal-scroll-wrapper">
            <Row gutter={48} align="middle">
              <Col xs={24} md={7}>
                <div className="modal-title">Primary contact</div>
              </Col>
            </Row>

            <FieldArray
              name="primaryContacts"
              component={PrimaryContactsFieldArray as any}
            />
          </div>

          <BaseModalFooter>
            <Button onClick={handleModalCancel}>Cancel</Button>

            <SubmitButton type="primary">Save</SubmitButton>
          </BaseModalFooter>
        </Form>
      </Formik>
    </BaseModal>
  );
}
